/**
 * Site-variant detection.
 *
 * The same React build is served from four domains. Three of them are
 * mirrors that need slightly different behavior:
 *   - ask.lovestoria.org — landing-only mirror; non-landing routes redirect
 *     back to the main domain. Order CTAs open Telegram instead of the
 *     checkout sidebar. Stripe branding is not shown.
 *   - ru.lovestoria.org — Russian-IP mirror. Default UI language forced to
 *     ru. Order CTAs open the Telegram channel. Stripe sidebar isn't
 *     rendered. Stripe branding on landing is hidden.
 *   - tr.lovestoria.org — landing-only mirror for Turkey. Default language
 *     forced to tr. Stripe checkout сохранён (Pixel-метка отдельная для
 *     учёта в Meta Ads).
 *
 * On the canonical lovestoria.org all variant flags return false and the
 * site behaves as before.
 */

export const ASK_HOSTNAME = 'ask.lovestoria.org';
export const RU_HOSTNAME = 'ru.lovestoria.org';
export const TR_HOSTNAME = 'tr.lovestoria.org';
export const MAIN_ORIGIN = 'https://lovestoria.org';

/** Telegram channel for the RU-mirror CTAs. */
export const TELEGRAM_USERNAME = 'lovestoriabooks';
export const TELEGRAM_URL = `https://t.me/${TELEGRAM_USERNAME}`;

/** Telegram редакторов — контакт, который даём юзеру после завершения книги. */
export const EDITORS_TELEGRAM_URL = 'https://t.me/lovestoriapublishing';

/** WhatsApp редакторов — второй канал связи после завершения книги. */
export const EDITORS_WHATSAPP_URL = 'https://wa.me/77011055680';

function hostname(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}

export function isAskVariant(): boolean {
  return hostname() === ASK_HOSTNAME;
}

export function isRuVariant(): boolean {
  return hostname() === RU_HOSTNAME;
}

export function isTrVariant(): boolean {
  return hostname() === TR_HOSTNAME;
}

/** Любое landing-only / mirror зеркало. Скрывает Stripe-логотип на лендинге
 *  там, где CTA либо ведёт в WhatsApp/Telegram, либо в чекаут с другой
 *  payment-логикой. */
export function isMirrorVariant(): boolean {
  return isAskVariant() || isRuVariant() || isTrVariant();
}

/** Build a URL on the main domain, preserving path/search/hash conventions. */
export function mainAppUrl(pathPlusQs: string): string {
  if (!pathPlusQs) return MAIN_ORIGIN;
  return MAIN_ORIGIN + (pathPlusQs.startsWith('/') ? pathPlusQs : '/' + pathPlusQs);
}

/** Resolve the chat-link CTA for the current host:
 *   ru   → Telegram channel
 *   else → caller's WhatsApp URL (passed in for backwards-compat with the
 *          existing buildWhatsappUrl(MANAGER_PHONE) call sites). */
export function chatLink(whatsappUrl: string): string {
  if (isRuVariant()) return TELEGRAM_URL;
  return whatsappUrl;
}

/** On landing-only mirror tr.lovestoria.org, internal SPA paths don't exist
 *  in the slim build, so any internal link must redirect to the canonical
 *  site as a full-page navigation. On other hosts the path is returned as-is
 *  so the SPA router handles it. */
export function navHref(path: string): string {
  if (isTrVariant()) return mainAppUrl(path);
  return path;
}
